/**
 * Staging-only panel: the portal's gas figures and the wallet's, side by side.
 *
 * It exists because the two disagreed and nobody could see it. Mode B funds a
 * wallet from the portal's own RPC quote; MetaMask prices the same transaction
 * from whichever node its network entry points at, and on chain 97 those nodes
 * quote gas ten times apart. The user meets that disagreement as a greyed-out
 * confirm button with no number on it, which is unarguable and undiagnosable at
 * the same time.
 *
 * So the panel asks both sides and shows the arithmetic. Its one job is to
 * separate the two explanations that look identical from the outside:
 *
 *     balance < what the wallet reserves   → a funding number is wrong
 *     balance ≥ what the wallet reserves   → the button is disabled for some
 *                                            other reason, and no amount of
 *                                            extra BNB will fix it
 *
 * Never rendered on chain 56. The gate is the chain id rather than a flag,
 * because a flag is something somebody can set by accident on the wrong host
 * and a chain id is not.
 */
import { useCallback, useEffect, useState } from "react";
import { encodeFunctionData, erc20Abi, formatEther, formatGwei } from "viem";
import type { Address, EIP1193Provider } from "viem";

import { CHAIN_ID, CLAIM_CONTRACT, LEGACY_TOKEN } from "@/lib/config";
import {
  DEFAULT_WALLET_MODEL,
  requiredBalanceWei,
  type SponsorPlan,
  type SponsorStatus,
} from "@/lib/gas-sponsor";
import {
  lastWalletRequest,
  onWalletRequest,
  readWalletGasView,
  walletRequirementWei,
  type RecordedRequest,
  type WalletGasView,
} from "@/lib/wallet-diagnostics";

export const DIAGNOSTICS_ENABLED = CHAIN_ID !== 56;

type Props = {
  address: Address | null;
  amountRaw: bigint | null;
  status: SponsorStatus | null;
  portalBalanceWei: bigint | null;
  portalGasEstimate: bigint | null;
  portalGasPriceWei: bigint | null;
  getProvider: () => EIP1193Provider | null;
  readPlan: () => Promise<SponsorPlan | null>;
};

const wei = (value: bigint | null | undefined) =>
  value === null || value === undefined ? "—" : `${formatEther(value)} BNB`;
const gwei = (value: bigint | null | undefined) =>
  value === null || value === undefined ? "—" : `${formatGwei(value)} gwei`;
const count = (value: bigint | number | null | undefined) =>
  value === null || value === undefined ? "—" : value.toLocaleString("id-ID");

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1 font-mono text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-right break-all text-slate-200">{value}</span>
    </div>
  );
}

export function GasDiagnostics({
  address,
  amountRaw,
  status,
  portalBalanceWei,
  portalGasEstimate,
  portalGasPriceWei,
  getProvider,
  readPlan,
}: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<WalletGasView | null>(null);
  const [plan, setPlan] = useState<SponsorPlan | null>(null);
  const [request, setRequest] = useState<RecordedRequest | null>(
    lastWalletRequest(),
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => onWalletRequest(setRequest), []);

  const model = status?.walletModel ?? DEFAULT_WALLET_MODEL;

  const refresh = useCallback(async () => {
    if (!address || !LEGACY_TOKEN || !CLAIM_CONTRACT) return;
    const provider = getProvider();
    if (!provider) return;
    setBusy(true);
    try {
      const data = encodeFunctionData({
        abi: erc20Abi,
        functionName: "approve",
        args: [CLAIM_CONTRACT, amountRaw ?? 0n],
      });
      setView(
        await readWalletGasView(provider, {
          from: address,
          to: LEGACY_TOKEN,
          data,
        }),
      );
    } finally {
      setBusy(false);
    }
  }, [address, amountRaw, getProvider]);

  if (!DIAGNOSTICS_ENABLED || !address) return null;

  const walletNeeds =
    view === null ? null : walletRequirementWei(view, model.gasLimitBps);
  const balance = view?.balanceWei ?? portalBalanceWei ?? null;
  const portalNeeds =
    portalGasEstimate !== null && portalGasPriceWei !== null
      ? requiredBalanceWei(portalGasEstimate, portalGasPriceWei, model)
      : null;

  const verdict =
    walletNeeds === null || balance === null
      ? "belum terbaca"
      : balance >= walletNeeds
        ? "saldo CUKUP untuk syarat dompet — tombol nonaktif bukan karena saldo"
        : `saldo KURANG ${formatEther(walletNeeds - balance)} BNB dari syarat dompet`;

  return (
    <div className="mt-4 rounded-lg border border-slate-700 bg-slate-900/60 p-3">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          /* Read on the toggle rather than from an effect: opening the panel is
             the event that wants fresh numbers, and an effect that fetches on
             every render of `open` is a cascading render for no extra truth. */
          if (next) void refresh();
        }}
        className="w-full text-left font-mono text-xs text-slate-400"
      >
        {open ? "▾" : "▸"} diagnostik gas (staging, chain {CHAIN_ID})
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <section>
            <p className="mb-1 font-mono text-[11px] uppercase text-slate-500">
              node dompet — angka yang dipakai MetaMask
            </p>
            <Row label="chainId" value={count(view?.chainId ?? null)} />
            <Row label="saldo" value={wei(view?.balanceWei)} />
            <Row label="eth_gasPrice" value={gwei(view?.gasPriceWei)} />
            <Row label="baseFeePerGas" value={gwei(view?.baseFeePerGasWei)} />
            <Row
              label="maxPriorityFeePerGas"
              value={gwei(view?.maxPriorityFeePerGasWei)}
            />
            <Row label="eth_estimateGas" value={count(view?.estimateGas)} />
            <Row
              label={`syarat dompet (${count(model.gasLimitBps / 100n)}%)`}
              value={wei(walletNeeds)}
            />
            {view?.errors.map((error) => (
              <Row key={error} label="error" value={error} />
            ))}
          </section>

          <section>
            <p className="mb-1 font-mono text-[11px] uppercase text-slate-500">
              portal — angka yang dipakai sponsorship
            </p>
            <Row label="saldo (RPC portal)" value={wei(portalBalanceWei)} />
            <Row label="estimateGas" value={count(portalGasEstimate)} />
            <Row label="gasPrice" value={gwei(portalGasPriceWei)} />
            <Row label="required (model dompet)" value={wei(portalNeeds)} />
            <Row
              label="walletGasLimitBps / walletFeeBps"
              value={`${count(model.gasLimitBps)} / ${count(model.feeBps)}`}
            />
          </section>

          {plan && (
            <section>
              <p className="mb-1 font-mono text-[11px] uppercase text-slate-500">
                server /api/gas/plan
              </p>
              <Row label="mode" value={plan.mode} />
              <Row label="approveGas" value={count(plan.approveGas)} />
              <Row label="gasPriceWei" value={gwei(BigInt(plan.gasPriceWei))} />
              <Row label="requiredWei" value={wei(BigInt(plan.requiredWei))} />
              <Row label="deficitWei" value={wei(BigInt(plan.deficitWei))} />
            </section>
          )}

          <section>
            <p className="mb-1 font-mono text-[11px] uppercase text-slate-500">
              permintaan terakhir ke dompet
            </p>
            {request ? (
              <>
                <Row label="method" value={request.method} />
                <Row label="from" value={request.from ?? "—"} />
                <Row label="to" value={request.to ?? "—"} />
                <Row label="value" value={request.value ?? "—"} />
                <Row label="gas" value={request.gas ?? "(tidak dikirim dapp)"} />
                <Row
                  label="gasPrice"
                  value={request.gasPrice ?? "(tidak dikirim dapp)"}
                />
                <Row
                  label="maxFeePerGas"
                  value={request.maxFeePerGas ?? "(tidak dikirim dapp)"}
                />
                <Row
                  label="maxPriorityFeePerGas"
                  value={request.maxPriorityFeePerGas ?? "(tidak dikirim dapp)"}
                />
                <Row label="calldata" value={`${request.dataBytes ?? 0} byte`} />
              </>
            ) : (
              <p className="font-mono text-xs text-slate-500">
                belum ada — tekan tombol approve dan panel ini akan mencatatnya
              </p>
            )}
          </section>

          <p className="font-mono text-xs text-amber-300">{verdict}</p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={busy}
              className="rounded border border-slate-600 px-2 py-1 font-mono text-xs text-slate-300 disabled:opacity-50"
            >
              baca ulang node dompet
            </button>
            <button
              type="button"
              onClick={() => void readPlan().then(setPlan)}
              className="rounded border border-slate-600 px-2 py-1 font-mono text-xs text-slate-300"
            >
              baca /api/gas/plan (perlu tanda tangan)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
